import axios from '@/libs/axios'
import { ApiCode } from '@/const/api'
import { handleCatch } from '@/utils'

/**
 * Lấy danh sách tài khoản với phân trang
 * @param {Object} params - Tham số tìm kiếm và phân trang
 * @returns {Promise<{records: Array, total: number}>}
 */
export const getAccounts = async (params) => {
  try {
    const response = await axios.get('uni/accounts', { params })
    const { data } = response
    if (data.code === ApiCode.SUCCESS) {
      const { total, records } = data.data
      return { records, total }
    }
    return { records: [], total: 0 }
  } catch (e) {
    handleCatch(e)
    return { records: [], total: 0 }
  }
}

/**
 * Lấy tất cả tài khoản (không phân trang) - dùng cho xuất excel
 * @param {Object} params - Tham số tìm kiếm
 * @returns {Promise<Array>}
 */
export const getAllAccounts = async (params) => {
  try {
    const response = await axios.get('uni/accounts', { params })
    const { data } = response
    if (data.code === ApiCode.SUCCESS) {
      const { records } = data.data
      return records || []
    }
    return []
  } catch (e) {
    handleCatch(e)
    return []
  }
}

/**
 * Lấy danh sách supervisor
 * @param {Object} params - Tham số tìm kiếm
 * @returns {Promise<{records: Array, total: number}>}
 */
export const getSupervisors = async (params) => {
  try {
    const response = await axios.get('uni/account/supervisor', { params })
    const { data } = response
    if (data.code === ApiCode.SUCCESS) {
      const { total, records } = data.data
      return { records, total }
    }
    return { records: [], total: 0 }
  } catch (e) {
    handleCatch(e)
    return { records: [], total: 0 }
  }
}

/**
 * Tạo mới tài khoản
 * @param {Object} params - Thông tin tài khoản
 * @returns {Promise<{isSuccessful: boolean, message: string}>}
 */
export const createAccount = async (params) => {
  try {
    const response = await axios.post('uni/accounts', params)
    const { data } = response
    return { isSuccessful: data.code === ApiCode.SUCCESS, message: data.message }
  } catch (e) {
    handleCatch(e)
    return { isSuccessful: false, message: 'Có lỗi xảy ra' }
  }
}

/**
 * Cập nhật thông tin tài khoản
 * @param {Object} params - Thông tin tài khoản
 * @returns {Promise<{isSuccessful: boolean, message: string}>}
 */
export const updateAccount = async (params) => {
  try {
    const response = await axios.put(`uni/accounts/${params.id}`, params)
    const { data } = response
    return { isSuccessful: data.code === ApiCode.SUCCESS, message: data.message }
  } catch (e) {
    handleCatch(e)
    return { isSuccessful: false, message: 'Có lỗi xảy ra' }
  }
}

/**
 * Xóa tài khoản
 * @param {number} id - ID tài khoản
 * @returns {Promise<{isSuccessful: boolean, message: string}>}
 */
export const deleteAccount = async (id) => {
  try {
    const response = await axios.delete(`uni/accounts/${id}`)
    const { data } = response
    return { isSuccessful: data.code === ApiCode.SUCCESS, message: data.message }
  } catch (e) {
    handleCatch(e)
    return { isSuccessful: false, message: 'Có lỗi xảy ra' }
  }
}

/**
 * Đặt lại mật khẩu
 * @param {Object} params - Thông tin đặt lại mật khẩu
 * @returns {Promise<{isSuccessful: boolean, message: string}>}
 */
export const resetPassword = async (params) => {
  try {
    const response = await axios.put(`uni/accounts/${params.id}/resetPassword`, params)
    const { data } = response
    return { isSuccessful: data.code === ApiCode.SUCCESS, message: data.message }
  } catch (e) {
    handleCatch(e)
    return { isSuccessful: false, message: 'Có lỗi xảy ra' }
  }
}

/**
 * Thay đổi mật khẩu
 * @param {Object} params - Thông tin thay đổi mật khẩu
 * @returns {Promise<{isSuccessful: boolean, message: string}>}
 */
export const changePassword = async (params) => {
  try {
    const response = await axios.put(`uni/accounts/${params.id}/changePassword`, params)
    const { data } = response
    return { isSuccessful: data.code === ApiCode.SUCCESS, message: data.message }
  } catch (e) {
    handleCatch(e)
    return { isSuccessful: false, message: 'Có lỗi xảy ra' }
  }
}

/**
 * Lấy danh sách vai trò của tài khoản
 * @param {number} id - ID tài khoản
 * @returns {Promise<Array>}
 */
export const getAccountRoles = async (id) => {
  try {
    const response = await axios.get(`uni/accounts/${id}/roles`)
    const { data } = response
    if (data.code === ApiCode.SUCCESS) {
      return data.data || []
    }
    return []
  } catch (e) {
    handleCatch(e)
    return []
  }
}

/**
 * Lưu vai trò cho tài khoản
 * @param {number} id - ID tài khoản
 * @param {Array} rolesAdded - Danh sách vai trò thêm mới
 * @param {Array} rolesDeleted - Danh sách vai trò xóa
 * @returns {Promise<{isSuccessful: boolean, message: string}>}
 */
export const saveAccountRoles = async (id, rolesAdded, rolesDeleted) => {
  try {
    const response = await axios.post(`uni/accounts/${id}/roles`, { rolesAdded, rolesDeleted })
    const { data } = response
    return { isSuccessful: data.code === ApiCode.SUCCESS, message: data.message }
  } catch (e) {
    handleCatch(e)
    return { isSuccessful: false, message: 'Có lỗi xảy ra' }
  }
}

/**
 * Hủy liên kết tài khoản
 * @param {number} id - ID tài khoản
 * @returns {Promise<{isSuccessful: boolean, message: string}>}
 */
export const unlinkAccount = async (id) => {
  try {
    const response = await axios.put(`uni/accounts/${id}/unlinkAccount`)
    const { data } = response
    return { isSuccessful: data.code === ApiCode.SUCCESS, message: data.message }
  } catch (e) {
    handleCatch(e)
    return { isSuccessful: false, message: 'Có lỗi xảy ra' }
  }
}

/**
 * Cấp quyền supervisor
 * @param {Object} params - Thông tin cấp quyền
 * @returns {Promise<{isSuccessful: boolean, message: string}>}
 */
export const grantSupervisor = async (params) => {
  try {
    const response = await axios.put('uni/account/supervisor/grant', params)
    const { data } = response
    return { isSuccessful: data.code === ApiCode.SUCCESS, message: data.message }
  } catch (e) {
    handleCatch(e)
    return { isSuccessful: false, message: 'Có lỗi xảy ra' }
  }
}

/**
 * Thu hồi quyền supervisor
 * @param {Object} params - Thông tin thu hồi quyền
 * @returns {Promise<{isSuccessful: boolean, message: string}>}
 */
export const revokeSupervisor = async (params) => {
  try {
    const response = await axios.put('uni/account/supervisor/revoke', params)
    const { data } = response
    return { isSuccessful: data.code === ApiCode.SUCCESS, message: data.message }
  } catch (e) {
    handleCatch(e)
    return { isSuccessful: false, message: 'Có lỗi xảy ra' }
  }
}
